import { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';

import {
  useLazyPublicDataQuery,
  useLazySpecificProductsQuery,
} from '@timum/timum_pdk';

import {
  selectAppConfig,
  setResToPublicDataMap,
  selectPublicData,
  setIsWaitingForConf,
} from '../../slices/appState';
import { pickDataFetchDeps } from '../../utils/configUtils';

const addPublicDataToMap = (data, map) => {
  if (!data) return;
  if (!data.forEach) {
    // no forEach means no list. Therefore we just add the data
    // I think this only happens in stories though....
    map[data.resource.uuid] = { ...data };
  } else {
    data.forEach((data) => {
      map[data.resource.uuid] = { ...data };
    });
  }
};

async function fetchReferencedData({
  t,
  appConfig,
  dispatch,
  fetchPublicData,
  fetchSpecificProducts,
}) {
  let map = {};

  const refPool = appConfig?.ref;
  const tslRefPool = appConfig?.tslRefs;
  const virtualTslRefPool = appConfig?.virtualTslRefs;
  const prdRefPool = appConfig?.prdRefs;
  const additionalData = appConfig?.additionalData;
  const allResourcesOption = appConfig?.allResourcesOption;

  const givenData = appConfig?.publicData;

  const channelKey = appConfig?.channelKey;

  if (
    (refPool?.length > 0 ||
      tslRefPool?.length > 0 ||
      virtualTslRefPool?.length > 0) &&
    !givenData?.length > 0
  ) {
    const publicDataResult = await fetchPublicData({
      params: {
        channelKey: channelKey,
        ref: refPool,
        tslRefs: tslRefPool,
        ...additionalData,
      },
      body: virtualTslRefPool,
    });
    addPublicDataToMap(publicDataResult?.data, map);
  }

  if (givenData?.length > 0) {
    addPublicDataToMap(givenData, map);
  }

  let prdResult = undefined;
  if (prdRefPool?.length > 0) {
    prdResult = await fetchSpecificProducts({
      params: {
        prdRefs: prdRefPool,
        channelKey: channelKey,
        additionalData,
      },
    });

    // now we assign the products to the fitting resources in the map.
    // we discard resources that have no matching products
    const sd = Object.entries(map).filter(([, publicData]) => {
      if (publicData?.resource?.supportedProducts?.length === 0) {
        return false; // discard
      }

      const prdMatches = prdResult.data.filter((prd) => {
        const supportedProducts = publicData?.resource?.supportedProducts;

        return (
          supportedProducts &&
          supportedProducts.some((prdUuid) => prdUuid === prd.uuid)
        );
      });

      if (prdMatches.length === 0) {
        return false; // discard
      }

      publicData.products = prdMatches;
      return true; // keep
    });

    map = Object.fromEntries(sd);
  }

  // add the all option if its activated or if condensedView multiResourceMode is enabled.
  const condensedMultiRes =
    appConfig?.calendarFrontendOptions?.condensedView?.multiResourceMode;
  if (
    (allResourcesOption || condensedMultiRes) &&
    (!tslRefPool || tslRefPool.length === 0) &&
    (!virtualTslRefPool || virtualTslRefPool.length === 0) &&
    refPool &&
    refPool.length > 1
  ) {
    map = {
      'all-option': {
        resource: { uuid: 'all-option', name: t('common.All') },
        provider: Object.values(map)?.[0]?.provider,
        products: prdResult?.data,
      },
      ...map,
    };
  }

  dispatch(setResToPublicDataMap(map));

  if (Object.values(map).length > 1) {
    // if it's a cancel link, in a multi resource scenario, then we preselct the resource with the cancelable appointment
    if (appConfig.cancelableAppointment) {
      let foundMatch = false;

      for (const data of Object.values(map)) {
        if (data?.resource?.uuid === appConfig.cancelableAppointment?.resId) {
          dispatch(selectPublicData(data));
          foundMatch = true;
        }
      }

      // we should still select one, even if there is no match
      if (!foundMatch) {
        dispatch(selectPublicData(Object.values(map)[0]));
      }
    } else {
      //if there's no cancelable appointment, we just select the first one
      dispatch(selectPublicData(Object.values(map)[0]));
    }
  } else if (Object.values(map).length === 1) {
    dispatch(selectPublicData(Object.values(map)[0]));
  } else {
    console.warn('Given reference(s) seems to be invalid.');
  }
}

const DataFetchHandler = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const appConfig = useSelector(selectAppConfig);

  const fetchingPublicDataFailed = useMemo(
    () => appConfig?.callbacks?.fetchingPublicDataFailed,
    [appConfig?.callbacks?.fetchingPublicDataFailed],
  );

  const [fetchPublicData, { isFetching: isFetchingPublicData, error }] =
    useLazyPublicDataQuery();

  const [fetchSpecificProducts, { isFetching: isFetchingSpecificProducts }] =
    useLazySpecificProductsQuery();

  // Extract data-dependent props that should trigger refetches
  // These are the props that affect which data is fetched from the API.
  // The field set lives in configUtils (DATA_FETCH_DEP_KEYS) so ConfigHandler's
  // product-reset guard stays in lockstep with it.
  const dataFetchDeps = useMemo(
    // pickDataFetchDeps reads exactly the fields listed below, so the granular
    // dep list is correct and keeps this memo referentially stable when they are
    // unchanged (depending on the whole `appConfig` would defeat that).
    () => pickDataFetchDeps(appConfig),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      appConfig?.ref,
      appConfig?.prdRefs,
      appConfig?.tslRefs,
      appConfig?.virtualTslRefs,
      appConfig?.channelKey,
      appConfig?.additionalParams,
      appConfig?.additionalData,
      appConfig?.publicData,
      appConfig?.platform,
      appConfig?.prvUuid,
      appConfig?.allResourcesOption,
      appConfig?.cancelableAppointment,
      appConfig?.host,
    ],
  );

  // Track the previous data-dependent props to detect actual changes
  const prevDataFetchDepsRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Cleanup effect to reset refs on unmount (handles React Strict Mode and remounting)
  useEffect(() => {
    return () => {
      prevDataFetchDepsRef.current = null;
      hasInitializedRef.current = false;
    };
  }, []);

  // Data fetching effect - only triggers when data-dependent props change
  useEffect(() => {
    // Don't fetch if we don't have the required refs
    if (
      !appConfig?.ref?.length &&
      !appConfig?.tslRefs?.length &&
      !appConfig?.virtualTslRefs?.length
    ) {
      return;
    }

    // Check if this is the initial render or if data-dependent props have changed
    const isInitialRender = !hasInitializedRef.current;
    const depsChanged = !isEqual(prevDataFetchDepsRef.current, dataFetchDeps);

    // Only fetch if this is the initial render OR if data-dependent props changed
    if (isInitialRender || depsChanged) {
      prevDataFetchDepsRef.current = dataFetchDeps;
      hasInitializedRef.current = true;

      fetchReferencedData({
        t,
        appConfig,
        dispatch,
        fetchPublicData,
        fetchSpecificProducts,
      });
    }
  }, [
    appConfig,
    dataFetchDeps,
    dispatch,
    fetchPublicData,
    fetchSpecificProducts,
    t,
  ]);

  // Update waiting state based on fetch status
  useEffect(() => {
    dispatch(
      setIsWaitingForConf(isFetchingPublicData || isFetchingSpecificProducts),
    );
  }, [dispatch, isFetchingPublicData, isFetchingSpecificProducts]);

  // Handle fetch errors
  if (error) {
    if (fetchingPublicDataFailed) {
      fetchingPublicDataFailed(error);
    }
  }

  return children;
};

DataFetchHandler.displayName = 'DataFetchHandler';
export default DataFetchHandler;
